"use strict";
/**
 * 内容层契约（站点设置 + 页面 + 区块）。
 *
 * 与渲染层 `PagePayload` 的区别：这里是**存储态**，`PagePayload` 是**渲染态**。
 * 一条页面记录按语言存一份（`pages` 的唯一键是 site_id + locale + path），
 * 区块 props 里存的是**该语言已解引用的纯字符串**，不是 `{en, zh, es, ar}` 对象。
 *
 * 这个选择是有意的：一条记录对应一种语言，客户改西班牙语不会动到英语。
 * 反过来把四种语言塞进同一个 props 里，一次编辑就要重写整块对象，
 * 而且页面语言与 props 里的语言一旦不一致（漏翻某一项），渲染端无从判断该信谁。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SHELL_AREAS = exports.SITE_COMPANY_LIMIT = exports.SITE_TITLE_LIMIT = exports.SITE_BUSINESS_LIMIT = exports.LOCALE_DISPLAYS = exports.MAX_BLOCKS_PER_PAGE = exports.PAGE_STATUSES = exports.THEME_FIXED_PATHS = exports.PAGE_KINDS = void 0;
exports.isLocaleDisplay = isLocaleDisplay;
exports.emptySeo = emptySeo;
exports.isPageKind = isPageKind;
exports.isPageStatus = isPageStatus;
exports.siteDisplayNames = siteDisplayNames;
exports.normalizeSiteSettings = normalizeSiteSettings;
const contact_1 = require("./contact");
exports.PAGE_KINDS = ['home', 'category', 'product', 'page', 'not_found'];
/**
 * 主题版式固定占用的路径：首页、产品列表、联系页。
 *
 * 这三条不是"客户建的页面"，而是版式的落点（`kind` 在库里同样是 `'page'`，
 * 靠路径区分）。删掉它们，页头导航与主题里那些指向 `/products`、`/contact`
 * 的假设会指向不存在的记录，站上表现就是"点菜单空白"。
 *
 * 放 contracts 而不是服务端：控制台要据此隐藏删除按钮，服务端要据此拒删。
 * 两处各写一份，早晚会出现"按钮能点、点了报错"或者更糟的"按钮能点、真删掉了"。
 */
exports.THEME_FIXED_PATHS = ['/', '/products', '/contact'];
exports.PAGE_STATUSES = ['draft', 'published'];
/** 单页区块上限。主题侧还有 maxPerPage，这里防的是"整页被压成 200 块拖垮渲染" */
exports.MAX_BLOCKS_PER_PAGE = 40;
/**
 * 语言入口的排布。
 *
 * `auto` 不是"随便"，是一条明确的产品判断：两种语言平铺（点一下就到，
 * 比展开下拉少一步），三种以上收成下拉（横排四个语言名会挤掉页头导航）。
 * 客户要的是例外情况——语言名特别长、页头本来就挤、或者反过来嫌下拉
 * 多一次点击 —— 所以留 `inline` / `menu` 两档手动指定。
 *
 * `hidden` 是"不显示"这一档，2026-11-03 补的。原来只有三档排布方式，
 * 等于假设"开了多语言的站一定想在页头挂个入口"——不成立：
 * 做过 SEO 的客户常常只留一个英文站加一个阿语镜像，让访客从搜索结果
 * 的 `hreflang` 进来，页头再挂一排语言名反而把首屏 CTA 挤掉。
 * 不做成独立布尔值 `showLocaleSwitcher`，是因为"不显示"和"怎么显示"
 * 是同一个决策的两种结果：拆成两个字段后会出现
 * "显示=关 + 布局=下拉"这种自相矛盾的存量数据，还得再写一段取谁优先。
 */
exports.LOCALE_DISPLAYS = ['auto', 'inline', 'menu', 'hidden'];
function isLocaleDisplay(v) {
    return typeof v === 'string' && exports.LOCALE_DISPLAYS.includes(v);
}
/**
 * 主营业务的长度上限。
 *
 * 120 不是随手取的：提示词里客户描述本来就截 200，两者加起来再长，
 * 模型会开始"平均"这些信息，图形反而更模糊。一句话足够说清一个厂做什么。
 */
exports.SITE_BUSINESS_LIMIT = 120;
/**
 * 对外网站名称的上限。
 *
 * 与站点内部名同宽：它要放进页头、和导航挤在同一行，60 个字已经足够长。
 * 再长不会让信息更清楚，只会把导航挤到第二行。
 */
exports.SITE_TITLE_LIMIT = 60;
/**
 * 公司名称的上限。
 *
 * 比网站名宽一些：带地域与组织形式后缀的法定名称本来就长
 * （"宁波市北仑区欧若拉精密工具有限公司"）。它只出现在页脚与结构化数据里，
 * 不占页头，宽一点没有版面代价。
 */
exports.SITE_COMPANY_LIMIT = 80;
// ── 站点外壳（页头 / 页脚）─────────────────────────────────────────────
/** 外壳块能放在哪。与主题的 `SHARED_REGIONS` 同一套枚举 */
exports.SHELL_AREAS = ['utility', 'header', 'footer'];
function emptySeo() {
    return { title: null, description: null, ogImageUrl: null, canonicalPath: null, noindex: false };
}
function isPageKind(v) {
    return typeof v === 'string' && exports.PAGE_KINDS.includes(v);
}
function isPageStatus(v) {
    return typeof v === 'string' && exports.PAGE_STATUSES.includes(v);
}
/**
 * 站点对外的两个名字，**已兜好底**。
 *
 * 回退链只有这一处：`settings` 里填过就用填的，没填就取租户名。
 * 平台侧的 `toContext`、渲染层的 `pg-lookup`、以及任何以后新增的出口都调它 ——
 * 回退链抄成两份的那天，"页头显示公司名、标题显示内部站名"这种不一致就回来了。
 *
 * 为什么不回退到 `sites.name`：那是内部叫法（"北美站"），
 * 拿它当页头品牌名，访客会看到一个只有老板自己看得懂的词。
 */
function siteDisplayNames(settings, tenantName) {
    const raw = settings ?? {};
    const pick = (v) => typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
    return {
        title: pick(raw['title']) ?? tenantName,
        company: pick(raw['company']) ?? tenantName,
    };
}
/** 站点设置从 sites.settings 这个 jsonb 里读，字段可能缺、可能是旧格式 */
function normalizeSiteSettings(raw, fallback) {
    const settings = raw ?? {};
    const branding = (settings['branding'] ?? {});
    const str = (v) => (typeof v === 'string' && v.trim() !== '' ? v : null);
    const locales = Array.isArray(settings['locales'])
        ? settings['locales'].filter((v) => typeof v === 'string')
        : [];
    return {
        siteId: fallback.siteId,
        name: fallback.name,
        /*
         * 两个"对外名字"都读 settings，缺省为 null（不是回退成站点名）。
         *
         * 回退发生在渲染层：`SiteContext.site.title/company` 里已经兜到租户名。
         * 这里如果也兜一次，控制台的输入框会显示一个客户从没填过的值，
         * 他改一个字就等于"确认"了那个默认值，之后再也分不清哪个是系统给的。
         */
        title: str(settings['title']),
        company: str(settings['company']),
        // 多行/多余空白收成一句：它会进提示词，换行在那里只会切碎语义
        business: str(settings['business'])?.replace(/\s+/g, ' ').trim() || null,
        // 默认语言必须在列表里，且排第一：语言切换器与 hreflang 都依赖这个顺序
        locales: locales.length > 0
            ? [fallback.defaultLocale, ...locales.filter((l) => l !== fallback.defaultLocale)]
            : [fallback.defaultLocale],
        defaultLocale: fallback.defaultLocale,
        // 老站点没有这个键：`auto` 就是它们本来的行为（两种平铺、三种以上下拉）
        localeDisplay: isLocaleDisplay(settings['localeDisplay']) ? settings['localeDisplay'] : 'auto',
        branding: {
            logoUrl: str(branding['logoUrl']),
            faviconUrl: str(branding['faviconUrl']),
            brandColor: str(branding['brandColor']),
        },
        /*
         * 联系方式复用 `normalizeContact`，不再在这里按字段抄一遍。
         *
         * 抄一遍的代价是：新增一列（比如这次的 `whatsapp`）要在两个地方都记得加，
         * 漏一处的结果是控制台存进去了、`normalizeSiteSettings` 又把它丢掉，
         * 客户看着自己填好的号码在前台消失。那边还顺手做了号码规范化 ——
         * 库里存什么形态由它决定，读的一方不该有第二套理解。
         */
        contact: (0, contact_1.normalizeContact)(settings['contact']),
        appearance: {
            // 这两个值在库里是 NOT NULL，但契约层不引 theme-kit（它要能被前端单独打包），
            // 所以这里只保证"非空字符串"，合法性与回退由平台侧与渲染层各自用注册表判定
            theme: str(fallback.theme) ?? 'aurora',
            variant: str(fallback.variant) ?? 'classic',
        },
    };
}
//# sourceMappingURL=content.js.map